// src/config/index.ts
//
// Loads global YAML config files and validates with Zod.

import rawLimits    from "./limits.yaml?raw";
import rawUI        from "./ui.yaml?raw";
import rawIndexPage from "./indexPage.yaml?raw";
import rawTabs      from "./tabs.yaml?raw";

import yaml from "yaml";
import { z } from "zod";

/* ────────────────────── parse YAML ─────────────────────── */
const parsedLimits    = yaml.parse(rawLimits);
const parsedUI        = yaml.parse(rawUI);
const parsedIndexPage = yaml.parse(rawIndexPage);
const parsedTabs      = yaml.parse(rawTabs);

/* ────────────────────── Zod schemas ────────────────────── */

/* limits.yaml */
const LimitsSchema = z.object({
  filter: z.object({
    previewRows: z.number().int().nonnegative(),
    downloadCap: z.number().int().nonnegative(),
  }),
  tableExplorer: z.object({
    pageSize: z.number().int().positive(),
    rowDisplayOptions: z.array(z.number().int().positive()).nonempty(),
  }),
  queryRunner: z.object({
    maxRows:  z.number().int().positive(),
    pageSize: z.number().int().positive(),
  }),
  csvDownload: z.object({
    maxRows: z.number().int().positive(),
  }),
});

/* ui.yaml */
const UISchema = z.object({
  dataTable: z.object({
    defaultColWidth: z.number().int().positive(),
    minColWidth:     z.number().int().positive(),
    rowHeight:       z.number().int().positive(),
    copyColumnWidth: z.number().int().positive(),
    formatCacheSize: z.number().int().positive(),
  }),
  toast: z.object({
    maxSimultaneous: z.number().int().nonnegative(),
    autoCloseMs:     z.number().int().nonnegative(),
  }),
  debounce: z.object({
    defaultMs: z.number().int().nonnegative(),
  }),
});

/* indexPage.yaml */
const IndexPageSchema = z.object({
  title:       z.string().min(1),
  description: z.string().min(1),
});

/* tabs.yaml  – visuals entry no longer embeds the visual list */
const CoreTab = z.object({
  key:       z.string().min(1),
  label:     z.string().min(1),
  icon:      z.string().min(1),
  type:      z.literal("core"),
  component: z.string().min(1),
});

const VisualTab = z.object({
  key:   z.string().min(1),
  label: z.string().min(1),
  icon:  z.string().min(1),
  type:  z.literal("visuals"),
});

const TabsSchema = z.array(
  z.discriminatedUnion("type", [CoreTab, VisualTab])
).min(1);

/* ────────────────────── validate & export ───────────────── */
export const limitsConfig     = LimitsSchema.parse(parsedLimits);
export const uiConfig         = UISchema.parse(parsedUI);
export const indexPageConfig  = IndexPageSchema.parse(parsedIndexPage);
export const tabsConfig       = TabsSchema.parse(parsedTabs);

export type LimitsConfig    = typeof limitsConfig;
export type UIConfig        = typeof uiConfig;
export type IndexPageConfig = typeof indexPageConfig;
export type TabsConfig      = typeof tabsConfig;
